// ============================================================================
// Contenu binaire servi A TRAVERS l'app gest (CHORE-GEST-BLOBS)
// ============================================================================
// Les fichiers gest (image de publication, piece jointe d'affichage) sont servis
// par un PROXY SERVEUR sous Bearer — jamais par un SAS (voir
// server/blobs/serve.ts pour les deux verrous : pare-feu stgsapps + MI gs-gest
// scope-container inapte a getUserDelegationKey).
//
// Consequence CLIENT : un `<img src>` / `window.open` NU n'envoie pas le token
// (-> JSON 401 a l'ecran, dogfood Marc 2026-06-12). On materialise donc des
// OBJECT URLs via le fetch authentifie de @gs/api-client.
//
// Glue d'app volontairement locale : la primitive partagee
// (`fetchAuthenticatedBlobObjectUrl`) vit dans @gs/api-client et n'est PAS
// reimplementee ici. Le cache/hook ci-dessous reprend sciemment le pattern de
// packages/feedback/src/client/attachmentContent.ts (meme decision, meme
// contraintes) ; une promotion des deux copies vers @gs/api-client est un chore
// de de-duplication a part entiere — hors scope ici (blast radius fbk).
//
// CACHE DE PAGE sans revoke, deliberement : un object URL n'expire jamais (pas
// de vignette cassee), le volume est borne (1 image par publication de la page)
// et tout est libere au dechargement de la page.

import { useEffect, useState } from 'react';
import { fetchAuthenticatedBlobObjectUrl } from '@gs/api-client';

const objectUrlCache = new Map<string, Promise<string | undefined>>();

/** Object URL (cache de page) d'un binaire servi par l'app — undefined si refus. */
export function getBlobObjectUrl(path: string): Promise<string | undefined> {
  let pending = objectUrlCache.get(path);
  if (!pending) {
    pending = fetchAuthenticatedBlobObjectUrl(path).then((u) => {
      // Echec (401 transitoire, 404, 503 storage absent) : ne PAS mettre en
      // cache — re-tentable au prochain rendu.
      if (!u) objectUrlCache.delete(path);
      return u;
    });
    objectUrlCache.set(path, pending);
  }
  return pending;
}

/**
 * Hook image : resout l'object URL (undefined pendant le chargement ET en cas
 * d'echec). L'appelant garde donc TOUJOURS son fallback (rien affiche, initiales,
 * affordance masquee) — c'est ce qui rend l'ecran inerte tant que l'infra
 * storage n'est pas posee (STOP David n°1), sans le moindre 500 visible.
 */
export function useBlobObjectUrl(path: string | null): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    setUrl(undefined);
    if (!path) return undefined;
    void getBlobObjectUrl(path).then((u) => { if (alive) setUrl(u); });
    return () => { alive = false; };
  }, [path]);
  return url;
}

/**
 * Telecharge un fichier sous son nom d'origine : fetch authentifie DEDIE (hors
 * cache de page — le blob est revoque sitot le clic emis), ancre `<a download>`
 * synthetique. JAMAIS de navigation vers l'URL d'app nue (elle n'emporte pas le
 * Bearer). Retourne false si echec (l'appelant toaste).
 */
export async function downloadBlobAs(path: string, filename: string): Promise<boolean> {
  const u = await fetchAuthenticatedBlobObjectUrl(path);
  if (!u) return false;
  const anchor = document.createElement('a');
  anchor.href = u;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(u);
  return true;
}
